/**
 * companyEngineConfig.tsx — config silnika formatek dla kartoteki Firma
 * (keystone unifikacja-formatek — mirror `contractorEngineConfig.tsx`).
 *
 * WSZYSTKIE 14 pól core firmy dostaje custom renderer — zero natywnej delegacji do
 * `ExtFieldValueInput` (mirror kontrahenta), więc gałąź natywna silnika jest martwa dla
 * tej kartoteki — `labelSource`/`coerceEmptyToNull` zostają kosmetyczne.
 *
 * Etykiety: `ctx.field.name` (definicja pola z `GET /system-modules/companies/fields`,
 * DB-driven), NIE i18n — zachowanie 1:1 z `companyCoreRenderers.tsx` (przed migracją).
 * `required={ctx.field.isRequired}` — wartość z metadanych.
 *
 * Różnice od kontrahenta (Firmy są prostsze — patrz nagłówek `CompanyDataTab.tsx`):
 *  - brak sekcji GDPR, brak TEXT[] outliers (tags/pkdCodes), brak NIP onBlur check.
 *  - `boolean` invalid (highlight ramki) ORAZ `error={ctx.getError?.(...)}` (tekst błędu
 *    pod polem) — ujednolicenie walidacji 2026-09; renderery pól tekstowych forwardują
 *    komunikat do `<Field error>`, tak jak kontrahent.
 *
 * Kanał `extra` (CompanyExtra) niesie WYŁĄCZNIE `initialPrimary` — D13 lock pola
 * `is_primary` (checkbox zablokowany na ON, gdy rekord pierwotnie był primary; raz
 * primary = zawsze primary, uncheck = no-op). Wcześniej przekazywane jako osobny prop
 * `initialPrimary` do `CompanyDataTab`/`COMPANY_CORE_RENDERERS`, teraz — jak kanał NIP
 * kontrahenta — żyje w `CompanyDetail.tsx` i wpływa do silnika przez `runtime.extra`.
 */

import { Checkbox } from '@/components/Checkbox';
import { CoreInput, type CoreInputProps } from '@/components/CoreInput';
import { GroupsInput } from '@/components/groups/GroupsInput';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type {
  CoreFieldConfig,
  FieldRenderer,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';

import { COMPANY_CORE_FIELD_SLUGS, type CompanyFormData } from './CompanyDataTab';

/**
 * Stan runtime specyficzny dla firmy, niesiony przez `FieldRenderContext.extra` /
 * `KartotekaRenderRuntime.extra` (silnik go nie interpretuje — patrz engine-config.ts).
 * D13: `is_primary` zablokowany na ON, gdy rekord pierwotnie był primary — `CompanyDetail.tsx`
 * trzyma `initialPrimary` w stanie (ustawiane z odpowiedzi GET, resetowane w trybie create).
 */
export interface CompanyExtra {
  initialPrimary: boolean;
}

function TextInput(props: Omit<CoreInputProps, 'variant'>) {
  return <CoreInput {...props} />;
}

// ─── Ground-truth fieldType per slug (z seeda, apps/api/lib/seed-system-modules.ts
// → companies.coreFields) — używany WYŁĄCZNIE przez fallback statyczny silnika
// (403 na metadanych modułu). Renderer i tak owns widget. ───────────────────────
const COMPANY_FIELD_TYPES: Record<string, string> = {
  // Wielokrotny wybór ze słownika (kolumna TEXT[] kodów) — patrz `groupsRenderer`.
  groups: 'multiselect',
  legal_name: 'varchar',
  short_name: 'varchar',
  tax_id: 'varchar',
  vat_eu: 'varchar',
  regon: 'varchar',
  krs: 'varchar',
  email: 'email',
  phone: 'varchar',
  // Serwer NIE waliduje formatu adresu WWW (tylko max 255) — oś formatu 'varchar', żeby domena
  // bez schematu (`www.firma.pl`) nie blokowała zapisu. Wizualnie input i tak jest `type="url"`.
  website: 'varchar',
  brand_color: 'varchar',
  default_vat_rate: 'numeric',
  default_payment_terms_days: 'integer',
  is_primary: 'boolean',
  is_active: 'boolean',
};

// ─── Mapa renderers (slug → FieldRenderer), portowane 1:1 z companyCoreRenderers.tsx ──

/**
 * Grupy ze słownika `company_groups` — wielokrotny wybór. Renderer TUTAJ, żeby o miejscu
 * pola decydował LAYOUT z buildera (wzorzec 1:1 z kontrahentami/pracownikami/użytkownikami).
 */
const groupsRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} colSpan={2}>
    <GroupsInput
      dictionaryCode="company_groups"
      value={ctx.form.groups}
      onChange={(next) => ctx.setField({ groups: next })}
      disabled={!ctx.editable}
      data-testid="company-groups"
    />
  </Field>
);

const legalNameRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('legalName')}>
    <TextInput
      testId="company-field-legalName"
      value={ctx.form.legalName}
      onChange={(v) => ctx.setField({ legalName: v })}
      disabled={!ctx.editable}
      maxLength={255}
      invalid={ctx.hasError?.('legalName')}
    />
  </Field>
);

const shortNameRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('shortName')}>
    <TextInput
      value={ctx.form.shortName ?? ''}
      onChange={(v) => ctx.setField({ shortName: v || null })}
      disabled={!ctx.editable}
      maxLength={100}
      invalid={ctx.hasError?.('shortName')}
    />
  </Field>
);

const taxIdRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('taxId')}>
    <TextInput
      testId="company-field-taxId"
      value={ctx.form.taxId ?? ''}
      onChange={(v) => ctx.setField({ taxId: v || null })}
      disabled={!ctx.editable}
      maxLength={20}
      invalid={ctx.hasError?.('taxId')}
    />
  </Field>
);

const vatEuRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('vatEu')}>
    <TextInput
      value={ctx.form.vatEu ?? ''}
      onChange={(v) => ctx.setField({ vatEu: v || null })}
      disabled={!ctx.editable}
      maxLength={20}
      invalid={ctx.hasError?.('vatEu')}
    />
  </Field>
);

const regonRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('regon')}>
    <TextInput
      value={ctx.form.regon ?? ''}
      onChange={(v) => ctx.setField({ regon: v || null })}
      disabled={!ctx.editable}
      maxLength={20}
      invalid={ctx.hasError?.('regon')}
    />
  </Field>
);

const krsRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('krs')}>
    <TextInput
      value={ctx.form.krs ?? ''}
      onChange={(v) => ctx.setField({ krs: v || null })}
      disabled={!ctx.editable}
      maxLength={20}
      invalid={ctx.hasError?.('krs')}
    />
  </Field>
);

const emailRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('email')}>
    <TextInput
      type="email"
      value={ctx.form.email ?? ''}
      onChange={(v) => ctx.setField({ email: v || null })}
      disabled={!ctx.editable}
      maxLength={255}
      invalid={ctx.hasError?.('email')}
    />
  </Field>
);

const phoneRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('phone')}>
    <TextInput
      value={ctx.form.phone ?? ''}
      onChange={(v) => ctx.setField({ phone: v || null })}
      disabled={!ctx.editable}
      maxLength={50}
      invalid={ctx.hasError?.('phone')}
    />
  </Field>
);

const websiteRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('website')}>
    <TextInput
      type="url"
      value={ctx.form.website ?? ''}
      onChange={(v) => ctx.setField({ website: v || null })}
      disabled={!ctx.editable}
      maxLength={255}
      invalid={ctx.hasError?.('website')}
    />
  </Field>
);

/**
 * Color picker zamiast varchar input (pole zadeklarowane jako varchar w seedzie).
 * ŚWIADOMY wyjątek §4.12 — biblioteka nie ma prymitywu próbnika koloru (`CoreInput`
 * nakłada style text-inputu, nie swatcha), więc goły `<input type="color">` zostaje;
 * jedyny wpis Firm w `eslint-suppressions.json`.
 */
const brandColorRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <input
      type="color"
      value={ctx.form.brandColor ?? '#1E90FF'}
      onChange={(e) => ctx.setField({ brandColor: e.target.value })}
      disabled={!ctx.editable}
      className="h-10 w-full rounded-lg border border-border-secondary"
      data-testid="company-field-brandColor"
    />
  </Field>
);

/** Number input, step 0.01, zakres 0-99.99 (stawka VAT) — prymityw §4.12. */
const defaultVatRateRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <TextInput
      type="number"
      step="0.01"
      min="0"
      max="99.99"
      value={ctx.form.defaultVatRate ?? ''}
      onChange={(v) => ctx.setField({ defaultVatRate: v || null })}
      disabled={!ctx.editable}
      testId="company-field-defaultVatRate"
    />
  </Field>
);

/** Number input, zakres 0-365 (termin płatności w dniach) — prymityw §4.12. */
const defaultPaymentTermsDaysRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <TextInput
      type="number"
      min="0"
      max="365"
      value={
        ctx.form.defaultPaymentTermsDays != null ? String(ctx.form.defaultPaymentTermsDays) : ''
      }
      onChange={(v) => ctx.setField({ defaultPaymentTermsDays: v ? Number(v) : null })}
      disabled={!ctx.editable}
      testId="company-field-defaultPaymentTermsDays"
    />
  </Field>
);

/**
 * D13: checkbox zablokowany na ON, gdy rekord pierwotnie był primary (raz primary =
 * zawsze primary; uncheck = no-op) — `ctx.extra?.initialPrimary`, wstrzykiwane przez
 * `CompanyDetail.tsx` (mirror kanału NIP kontrahenta).
 */
const isPrimaryRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <Checkbox
      checked={ctx.form.isPrimary}
      disabled={!ctx.editable || (ctx.extra?.initialPrimary ?? false)}
      onChange={(e) => ctx.setField({ isPrimary: e.target.checked })}
      data-testid="company-field-isPrimary"
    />
  </Field>
);

const isActiveRenderer: FieldRenderer<CompanyFormData, CompanyExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <Checkbox
      checked={ctx.form.isActive}
      disabled={!ctx.editable}
      onChange={(e) => ctx.setField({ isActive: e.target.checked })}
      data-testid="company-field-isActive"
    />
  </Field>
);

const COMPANY_RENDERERS: Record<string, FieldRenderer<CompanyFormData, CompanyExtra>> = {
  groups: groupsRenderer,
  legal_name: legalNameRenderer,
  short_name: shortNameRenderer,
  tax_id: taxIdRenderer,
  vat_eu: vatEuRenderer,
  regon: regonRenderer,
  krs: krsRenderer,
  email: emailRenderer,
  phone: phoneRenderer,
  website: websiteRenderer,
  brand_color: brandColorRenderer,
  default_vat_rate: defaultVatRateRenderer,
  default_payment_terms_days: defaultPaymentTermsDaysRenderer,
  is_primary: isPrimaryRenderer,
  is_active: isActiveRenderer,
};

const fields: Record<string, CoreFieldConfig<CompanyFormData, CompanyExtra>> = {};
for (const slug of Object.keys(COMPANY_CORE_FIELD_SLUGS)) {
  const stateKey = COMPANY_CORE_FIELD_SLUGS[slug]!;
  fields[slug] = {
    stateKey,
    fieldType: COMPANY_FIELD_TYPES[slug] ?? 'varchar',
    renderer: COMPANY_RENDERERS[slug]!,
    // Jedyne pole z colSpan=2 (rysuje go zresztą samo na swoim <Field> — wpis tu jest
    // kosmetyczny, patrz komentarz nagłówka pliku, gałąź natywna silnika jest martwa
    // dla tej kartoteki).
    ...(slug === 'groups' ? { colSpan: 2 as const } : {}),
  };
}
// Pre-existing gap (parytet z przed-silnikowym CompanyDataTab) — `object_type` i `sort_order` są
// realnymi polami core w katalogu modułu `companies` (mają `section`, więc SĄ w domyślnym layoucie
// zakładki „Dane"), ale nie mają odpowiednika w `CompanyFormData` ani w `COMPANY_CORE_FIELD_SLUGS`
// (pętla wyżej ich nie widzi). Stary `CompanyDataTab` renderował je przez `return null` (brak w
// `COMPANY_CORE_RENDERERS`). Dopisane RĘCZNIE ze `structuralNull`, żeby silnik zwrócił `null`
// zamiast bursztynowego fallbacku „unsupportedType" (mirror `fields.is_active`/`gdpr_consent_at`
// w `contactEngineConfig.tsx`).
fields.object_type = {
  fieldType: 'select',
  structuralNull: true,
};
fields.sort_order = {
  fieldType: 'integer',
  structuralNull: true,
};

export const companyEngineConfig: KartotekaEngineConfig<CompanyFormData, CompanyExtra> = {
  moduleSlug: 'companies',
  // LOAD-BEARING: engine's ext delegation emits `${testIdPrefix}-${slug}` — testid
  // `company-ext-<slug>` (zachowanie 1:1 z przed-silnikowym CompanyDataTab).
  testIdPrefix: 'company-ext',
  coreFieldSlugs: Object.keys(COMPANY_CORE_FIELD_SLUGS),
  fields,
  coerceEmptyToNull: false,
};
